import React, { useState } from 'react';
import Layout from './Layout';
import { Play, BarChart3, TrendingUp, Settings } from 'lucide-react';

const ScenarioModeling: React.FC = () => {
  const [selectedScenario, setSelectedScenario] = useState('baseline');
  
  const scenarios = {
    baseline: {
      name: 'Current Operations',
      description: 'Existing biogas integration and waste processing',
      metrics: {
        co2Reduction: '2,847 tCO₂e',
        coalDisplacement: '892 tons',
        wasteProcessed: '4,567 tons',
        revenue: '$1.2M',
        efficiency: '89.2%'
      }
    },
    optimized: {
      name: 'Optimized Integration',
      description: 'Enhanced biogas production and improved EAF efficiency',
      metrics: {
        co2Reduction: '4,234 tCO₂e',
        coalDisplacement: '1,345 tons',
        wasteProcessed: '6,789 tons',
        revenue: '$1.8M',
        efficiency: '94.7%'
      }
    },
    expansion: {
      name: 'Multi-Plant Expansion',
      description: 'Scale to 10 steel plants and 5 cement facilities',
      metrics: {
        co2Reduction: '12,567 tCO₂e',
        coalDisplacement: '4,234 tons',
        wasteProcessed: '18,945 tons',
        revenue: '$5.2M',
        efficiency: '91.8%'
      }
    },
    international: {
      name: 'International Scale',
      description: 'Expansion to Kenya, South Africa, and Sweden partnerships',
      metrics: {
        co2Reduction: '45,678 tCO₂e',
        coalDisplacement: '15,234 tons',
        wasteProcessed: '67,890 tons',
        revenue: '$18.7M',
        efficiency: '93.4%'
      }
    }
  };

  const modelingParameters = [
    { name: 'Waste Input Volume', current: '4,567 tons', range: '2,000 - 20,000 tons' },
    { name: 'Biogas Production Rate', current: '0.27 MWh/ton', range: '0.20 - 0.35 MWh/ton' },
    { name: 'EAF Integration Efficiency', current: '89.2%', range: '75% - 95%' },
    { name: 'Coal Displacement Rate', current: '45%', range: '30% - 60%' },
    { name: 'Carbon Credit Price', current: '$25/tCO₂e', range: '$15 - $50/tCO₂e' }
  ];

  const impactProjections = [
    {
      timeframe: '6 Months',
      co2Avoided: '1,423 tCO₂e',
      revenue: '$356K',
      plants: '2 Steel, 1 Cement',
      readiness: 'TRL 5'
    },
    {
      timeframe: '1 Year',
      co2Avoided: '4,567 tCO₂e',
      revenue: '$1.1M',
      plants: '4 Steel, 2 Cement',
      readiness: 'TRL 6'
    },
    {
      timeframe: '2 Years',
      co2Avoided: '12,345 tCO₂e',
      revenue: '$3.1M',
      plants: '8 Steel, 4 Cement',
      readiness: 'Commercial'
    },
    {
      timeframe: '5 Years',
      co2Avoided: '45,678 tCO₂e',
      revenue: '$11.4M',
      plants: '20 Steel, 10 Cement',
      readiness: 'International'
    }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Scenario Modeling</h1>
          <button 
            onClick={() => window.alert('Running simulation...\n\nSimulation complete!\n\nResults:\n- Projected emissions: 31,200 tCO₂e by 2030\n- Carbon credits: 18,500 ITMOs\n- Investment required: $8.5M\n- ROI: 3.2 years\n- Compliance status: On track')}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Play className="h-4 w-4 mr-2" />
            Run Simulation
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {Object.entries(scenarios).map(([key, scenario]) => (
            <div
              key={key}
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                selectedScenario === key
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedScenario(key)}
            >
              <h3 className="font-medium text-gray-900 mb-2">{scenario.name}</h3>
              <p className="text-sm text-gray-600">{scenario.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Scenario Results: {scenarios[selectedScenario as keyof typeof scenarios].name}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {Object.entries(scenarios[selectedScenario as keyof typeof scenarios].metrics).map(([key, value]) => (
              <div key={key} className="text-center">
                <p className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Modeling Parameters</h2>
            <div className="space-y-4">
              {modelingParameters.map((param, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium text-gray-900">{param.name}</h3>
                    <span className="text-sm font-medium text-blue-600">{param.current}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Range:</span>
                    <span className="text-xs text-gray-600">{param.range}</span>
                  </div>
                  <div className="mt-2">
                    <input
                      type="range"
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      defaultValue="50"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Impact Projections</h2>
            <div className="space-y-4">
              {impactProjections.map((projection, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium text-gray-900">{projection.timeframe}</h3>
                    <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                      {projection.readiness}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">CO₂ Avoided:</span>
                      <span className="ml-2 font-medium text-green-600">{projection.co2Avoided}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Revenue:</span>
                      <span className="ml-2 font-medium text-blue-600">{projection.revenue}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Plants:</span>
                      <span className="ml-2 font-medium">{projection.plants}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment & Sensitivity Analysis</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="border rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                Technical Risks
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Biogas Quality Variation</span>
                  <span className="font-medium text-yellow-600">Medium</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">EAF Integration Complexity</span>
                  <span className="font-medium text-green-600">Low</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Scale-up Challenges</span>
                  <span className="font-medium text-yellow-600">Medium</span>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                Market Risks
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Carbon Credit Price Volatility</span>
                  <span className="font-medium text-red-600">High</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Steel Demand Fluctuation</span>
                  <span className="font-medium text-yellow-600">Medium</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Regulatory Changes</span>
                  <span className="font-medium text-green-600">Low</span>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-purple-600" />
                Operational Risks
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Waste Supply Consistency</span>
                  <span className="font-medium text-yellow-600">Medium</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plant Maintenance Downtime</span>
                  <span className="font-medium text-green-600">Low</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Staff Training Requirements</span>
                  <span className="font-medium text-yellow-600">Medium</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Scenario Comparison Matrix</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scenario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CO₂ Reduction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue Potential
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Implementation Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Risk Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRL Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Current Operations
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">2,847 tCO₂e</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">$1.2M</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Immediate</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      Low
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">TRL 5-6</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Optimized Integration
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">4,234 tCO₂e</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">$1.8M</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">6 months</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Medium
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">TRL 6</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Multi-Plant Expansion
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">12,567 tCO₂e</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">$5.2M</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">18 months</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Medium
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Commercial</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    International Scale
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">45,678 tCO₂e</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">$18.7M</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">3-5 years</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      High
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">International</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ScenarioModeling;